import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  ShieldCheck,
  TrendingUp,
  Users,
  Search,
  PiggyBank,
  GraduationCap,
  ArrowRight,
  Menu,
  X
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user } = useAuth();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-brand-dark selection:bg-brand-blue selection:text-white">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/80 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
                src="https://customer-assets.emergentagent.com/job_e9aa37b0-2814-4b9e-8c4c-03a6b3b48503/artifacts/hffkvssr_LogoAppadrino_color.png" 
                alt="Appadrino Logo" 
                className="h-12 w-auto" 
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('como-funciona')} className="text-gray-600 hover:text-brand-blue font-medium transition-colors">Cómo funciona</button>
            <button onClick={() => scrollToSection('beneficios')} className="text-gray-600 hover:text-brand-blue font-medium transition-colors">Beneficios</button>
            <button onClick={() => scrollToSection('testimonios')} className="text-gray-600 hover:text-brand-blue font-medium transition-colors">Testimonios</button>
            <Link to="/educacion" data-testid="nav-educacion-link" className="text-gray-600 hover:text-brand-blue font-medium transition-colors">Educación</Link>
            <div className="flex items-center gap-4 ml-4">
              {user ? (
                <Link to="/dashboard">
                    <Button className="bg-brand-blue text-white font-bold rounded-full px-6">
                        Ir al Dashboard
                    </Button>
                </Link>
              ) : (
                <>
                    <Link to="/login">
                        <Button variant="ghost" className="font-bold text-brand-blue hover:text-brand-blue/80 hover:bg-brand-lightBlue">
                        Ingresar
                        </Button>
                    </Link>
                    <Link to="/register">
                        <Button className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold rounded-full px-6 shadow-lg shadow-brand-green/20">
                        Crear Cuenta
                        </Button>
                    </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X className="w-6 h-6 text-brand-dark" /> : <Menu className="w-6 h-6 text-brand-dark" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 w-full bg-white border-b border-gray-100 p-4 flex flex-col gap-4 shadow-xl">
            <button onClick={() => scrollToSection('como-funciona')} className="text-left p-2 text-gray-600 font-medium">Cómo funciona</button>
            <button onClick={() => scrollToSection('beneficios')} className="text-left p-2 text-gray-600 font-medium">Beneficios</button>
            <button onClick={() => scrollToSection('testimonios')} className="text-left p-2 text-gray-600 font-medium">Testimonios</button>
            <Link to="/educacion" className="text-left p-2 text-gray-600 font-medium">Educación</Link>
            <div className="flex flex-col gap-3 mt-2">
              {user ? (
                <Link to="/dashboard">
                    <Button className="w-full bg-brand-blue text-white font-bold">
                        Ir al Dashboard
                    </Button>
                </Link>
              ) : (
                <>
                    <Link to="/login">
                        <Button variant="outline" className="w-full border-brand-blue text-brand-blue font-bold">
                        Ingresar
                        </Button>
                    </Link>
                    <Link to="/register">
                        <Button className="w-full bg-brand-green text-brand-dark font-bold">
                        Crear Cuenta
                        </Button>
                    </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden bg-gradient-to-b from-brand-lightBlue/30 to-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-block px-4 py-2 bg-brand-lightBlue rounded-full text-brand-blue font-bold text-sm mb-2">
                🎓 El futuro de tus hijos empieza hoy
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold leading-tight text-brand-dark">
                Asegura su educación <br/>
                <span className="text-brand-blue">sin deudas</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
                La forma más inteligente y colaborativa de construir el fondo educativo de tus hijos. Invita a padrinos y familia a sumar mes a mes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/register">
                    <Button className="h-14 px-8 text-lg bg-brand-blue hover:bg-brand-blue/90 text-white rounded-full shadow-xl shadow-brand-blue/20 transition-transform hover:scale-105">
                    Comenzar ahora
                    <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                </Link>
              </div>
              {/* ... social proof ... */}
            </div>
            <div className="relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-brand-lightBlue/50 rounded-full blur-3xl -z-10"></div>
              <video
                data-testid="hero-video"
                src="/videos/appadrino-demo.mp4"
                poster="/videos/appadrino-demo-poster.jpg"
                controls
                playsInline
                preload="metadata"
                className="w-full h-auto rounded-3xl shadow-2xl bg-black aspect-video object-cover"
              >
                Tu navegador no soporta la reproducción de video.
              </video>
              {/* Floating Element */}
              <div className="absolute -top-6 -left-6 bg-white p-4 rounded-2xl shadow-xl animate-bounce duration-[3000ms]">
                <div className="flex items-center gap-3">
                  <div className="bg-brand-green/20 p-2 rounded-full text-brand-green">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-bold">Rendimiento anual</p>
                    <p className="text-lg font-bold text-brand-dark">+12.5%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ... How it Works, Benefits, Testimonials ... (Truncated for brevity, assuming standard blocks) */}
      
      {/* How it Works Section */}
      <section id="como-funciona" className="py-24 bg-white relative">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-brand-blue font-bold tracking-wider uppercase mb-3">Cómo funciona</h2>
            <h3 className="text-3xl md:text-4xl font-extrabold text-brand-dark mb-6">Ahorrar en familia es más fácil</h3>
            <p className="text-gray-600 text-lg">En tres simples pasos, crea un futuro sólido para la educación universitaria.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Users className="w-8 h-8 text-brand-blue" />,
                title: "1. Aportas",
                desc: "Configura tu aporte mensual y el de los padrinos. Pequeñas sumas hacen grandes diferencias.",
                color: "bg-blue-50"
              },
              {
                icon: <PiggyBank className="w-8 h-8 text-brand-green" />,
                title: "2. Creamos la alcancía",
                desc: "Tu dinero se guarda en un fideicomiso seguro y transparente a nombre de tu hijo.",
                color: "bg-green-50"
              },
              {
                icon: <TrendingUp className="w-8 h-8 text-purple-600" />,
                title: "3. Crece mes a mes",
                desc: "Tus ahorros generan rendimientos compuestos hasta que llegue el momento de la universidad.",
                color: "bg-purple-50"
              }
            ].map((step, idx) => (
              <div key={idx} className="relative group p-8 rounded-3xl bg-white border border-gray-100 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                <div className={`w-16 h-16 rounded-2xl ${step.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  {step.icon}
                </div>
                <h4 className="text-xl font-bold mb-3 text-brand-dark">{step.title}</h4>
                <p className="text-gray-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-24 bg-brand-lightBlue/30">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 md:order-1">
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-brand-green/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-brand-blue/20 rounded-full blur-3xl"></div>
                <img 
                    src="https://images.unsplash.com/photo-1704708798525-5fe58553d801?auto=format&fit=crop&q=80&w=800" 
                    alt="Education Benefits" 
                    className="relative rounded-3xl shadow-2xl z-10"
                />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark mb-8">
                Por qué elegir <span className="text-brand-blue">Appadrino</span>?
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: "Seguridad Garantizada",
                    desc: "Tu dinero está en instituciones reguladas y supervisadas.",
                    icon: <ShieldCheck className="w-6 h-6 text-brand-green" />
                  },
                  {
                    title: "Total Transparencia",
                    desc: "Sin letras chiquitas. Monitorea el crecimiento de tu fondo 24/7.",
                    icon: <Search className="w-6 h-6 text-brand-blue" />
                  },
                  {
                    title: "Comunidad",
                    desc: "Una red de apoyo familiar enfocada en el éxito del estudiante.",
                    icon: <Users className="w-6 h-6 text-purple-500" />
                  },
                  {
                    title: "Alto Rendimiento",
                    desc: "Tasas competitivas que superan la inflación tradicional.",
                    icon: <TrendingUp className="w-6 h-6 text-orange-500" />
                  }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4 p-4 rounded-2xl bg-white/50 hover:bg-white transition-colors">
                    <div className="mt-1 bg-white p-2 rounded-lg shadow-sm h-fit">
                        {item.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-brand-dark">{item.title}</h4>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonios" className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center text-brand-dark mb-16">
            Familias felices
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Maria González",
                role: "Madre de Sofía",
                text: "Empezamos con poquito, pero ver cómo los abuelos y tíos se suman cada mes es increíble. Sofía tendrá su universidad asegurada.",
                avatar: "https://i.pravatar.cc/150?img=5"
              },
              {
                name: "Carlos Ruiz",
                role: "Padrino de Mateo",
                text: "Es el mejor regalo que puedo darle a mi ahijado. Mucho mejor que juguetes que terminan olvidados. Es regalar futuro.",
                avatar: "https://i.pravatar.cc/150?img=11"
              },
              {
                name: "Ana y Pedro",
                role: "Padres primerizos",
                text: "La plataforma es súper fácil de usar y nos da mucha paz mental saber que estamos construyendo esto desde ahora.",
                avatar: "https://i.pravatar.cc/150?img=8"
              }
            ].map((t, idx) => (
              <Card key={idx} className="border-none shadow-lg bg-gray-50/50 hover:bg-white transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4 mb-4">
                    <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <p className="font-bold text-brand-dark">{t.name}</p>
                      <p className="text-sm text-brand-blue">{t.role}</p>
                    </div>
                  </div>
                  <div className="flex mb-3">
                    {[1,2,3,4,5].map(star => (
                        <span key={star} className="text-yellow-400">★</span>
                    ))}
                  </div>
                  <p className="text-gray-600 italic">"{t.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-blue text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute right-0 bottom-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
            <div className="absolute left-0 top-0 w-64 h-64 bg-brand-green rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-6">
            ¿Listo para construir su futuro?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Únete a la comunidad de Appadrino y recibe nuestra guía gratuita de "Finanzas para niños" al registrarte.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
                <Button className="h-14 px-8 text-lg bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold rounded-full shadow-lg">
                Crear mi cuenta gratis
                </Button>
            </Link>
            <Button variant="outline" className="h-14 px-8 text-lg bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full font-bold">
              Descargar Guía PDF
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 py-12 border-t border-gray-200">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <GraduationCap className="w-6 h-6 text-brand-blue" />
                <span className="text-xl font-bold text-brand-blue">Appadrino</span>
              </div>
              <p className="text-gray-500 text-sm">
                Plataforma de ahorro colaborativo para la educación superior.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-brand-dark mb-4">Producto</h4>
              <ul className="space-y-2 text-gray-500 text-sm">
                <li><a href="#" className="hover:text-brand-blue">Cómo funciona</a></li>
                <li><a href="#" className="hover:text-brand-blue">Seguridad</a></li>
                <li><a href="#" className="hover:text-brand-blue">Rendimientos</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-brand-dark mb-4">Compañía</h4>
              <ul className="space-y-2 text-gray-500 text-sm">
                <li><a href="#" className="hover:text-brand-blue">Nosotros</a></li>
                <li><Link to="/educacion" className="hover:text-brand-blue">Blog</Link></li>
                <li><a href="#" className="hover:text-brand-blue">Contacto</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-brand-dark mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-500 text-sm">
                <li><a href="#" className="hover:text-brand-blue">Términos y condiciones</a></li>
                <li><a href="#" className="hover:text-brand-blue">Política de privacidad</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-8 text-center text-sm text-gray-400">
            © 2025 Appadrino. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
